
import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePremiumDialog } from '@/contexts/PremiumDialogContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useIsMobile } from '@/hooks/use-mobile';

export const PremiumDialog: React.FC = () => {
  const { isOpen, isProcessing, closeDialog, handleSubscribe } = usePremiumDialog();
  const isMobile = useIsMobile();
  
  // Handle ESC key press
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isProcessing) {
        closeDialog();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, isProcessing, closeDialog]);

  // Handle focus trap
  useEffect(() => {
    const dialogElement = document.querySelector('[role="dialog"]');
    if (!dialogElement || !isOpen) return;

    const focusableElements = dialogElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusableElement = focusableElements[0] as HTMLElement;
    const lastFocusableElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          event.preventDefault();
          lastFocusableElement.focus();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          event.preventDefault();
          firstFocusableElement.focus();
        }
      }
    };

    dialogElement.addEventListener('keydown', handleTabKey);
    firstFocusableElement.focus();

    return () => {
      dialogElement.removeEventListener('keydown', handleTabKey);
    };
  }, [isOpen]);

  const variants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.2
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.95,
      transition: {
        duration: 0.15
      }
    }
  };

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        if (!open && !isProcessing) {
          closeDialog();
        }
      }}
    >
      <DialogContent 
        className={`bg-[#1A1F2C] border border-primary/20 text-white z-[100] ${isMobile ? 'max-w-[95%] max-h-[90vh] overflow-y-auto p-3' : 'max-w-2xl'}`}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          // Return focus to the last focused element before dialog opened
          const lastFocusedElement = document.querySelector('[data-last-focused="true"]') as HTMLElement;
          if (lastFocusedElement) {
            lastFocusedElement.focus();
            lastFocusedElement.removeAttribute('data-last-focused');
          }
        }}
      >
        <DialogHeader className="border-b border-primary/10 pb-3">
          <DialogTitle className={`${isMobile ? 'text-2xl' : 'text-4xl'} font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent flex items-center gap-2`}>
            Super Hyper Remix <span className="text-primary">✨</span>
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Level up your profile with exclusive features
          </DialogDescription>
        </DialogHeader>
        
        <AnimatePresence mode="wait" onExitComplete={() => {
          console.log('[Premium Dialog] Animation exit complete');
        }}>
          <motion.div 
            key="premium-content"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={variants}
            className={`space-y-4 ${isMobile ? 'py-3' : 'space-y-6 py-6'}`}
            layoutId="premium-dialog-content"
          >
            <div className={`space-y-${isMobile ? '2' : '4'} divide-y divide-primary/10`}>
              <motion.div 
                className={`flex items-start gap-3 pb-${isMobile ? '3' : '4'}`}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                <div className={`${isMobile ? 'text-xl' : 'text-2xl'} bg-primary/10 p-3 rounded-lg`}>💠</div>
                <div>
                  <h3 className="font-semibold text-primary">Avatar Shimmer</h3>
                  <p className="text-muted-foreground text-sm">Glow like a rare holographic loot drop</p>
                </div>
              </motion.div>
              
              <motion.div 
                className={`flex items-start gap-3 pt-${isMobile ? '3' : '4'} pb-${isMobile ? '3' : '4'}`}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className={`${isMobile ? 'text-xl' : 'text-2xl'} bg-primary/10 p-3 rounded-lg`}>💥</div>
                <div>
                  <h3 className="font-semibold text-primary">Avatar Stroke</h3>
                  <p className="text-muted-foreground text-sm">A bold aura that demands attention</p>
                </div>
              </motion.div>

              <motion.div 
                className={`flex items-start gap-3 pt-${isMobile ? '3' : '4'} pb-${isMobile ? '3' : '4'}`}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <div className={`${isMobile ? 'text-xl' : 'text-2xl'} bg-primary/10 p-3 rounded-lg`}>⚡</div>
                <div>
                  <h3 className="font-semibold text-primary">Post Borders</h3>
                  <p className="text-muted-foreground text-sm">Frame your content with legendary glow</p>
                </div>
              </motion.div>

              <motion.div 
                className={`flex items-start gap-3 pt-${isMobile ? '3' : '4'} pb-${isMobile ? '3' : '4'}`}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <div className={`${isMobile ? 'text-xl' : 'text-2xl'} bg-primary/10 p-3 rounded-lg`}>🎞</div>
                <div>
                  <h3 className="font-semibold text-primary">GIF & MP4 Posting</h3>
                  <p className="text-muted-foreground text-sm">Bring your posts to life</p>
                </div>
              </motion.div>

              <motion.div 
                className={`flex items-start gap-3 pt-${isMobile ? '3' : '4'} pb-${isMobile ? '3' : '4'}`}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <div className={`${isMobile ? 'text-xl' : 'text-2xl'} bg-primary/10 p-3 rounded-lg`}>🌈</div>
                <div>
                  <h3 className="font-semibold text-primary">Unique Username Color</h3>
                  <p className="text-muted-foreground text-sm">Stand out instantly</p>
                </div>
              </motion.div>

              <motion.div 
                className={`flex items-start gap-3 pt-${isMobile ? '3' : '4'}`}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <div className={`${isMobile ? 'text-xl' : 'text-2xl'} bg-primary/10 p-3 rounded-lg`}>🛠</div>
                <div>
                  <h3 className="font-semibold text-primary">More Features Incoming</h3>
                  <p className="text-muted-foreground text-sm">This is just the first DLC—expect OP perks!</p>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="pt-2"
            >
              <Button 
                onClick={handleSubscribe}
                disabled={isProcessing}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
              >
                {isProcessing ? "Processing..." : "Subscribe Now ✨"}
              </Button>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
